import { useCallback, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface Column<T> {
  id: string
  header: string
  cell: (row: T) => ReactNode
  sortable?: boolean
  className?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (row: T) => string
  onRowClick?: (row: T) => void
  rowLabel?: (row: T) => string
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (field: string, order: 'asc' | 'desc') => void
  page?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  totalItems?: number
  pageSize?: number
  itemLabel?: string
  loading?: boolean
  fetching?: boolean
  emptyMessage?: string
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  rowLabel,
  sortField,
  sortOrder = 'asc',
  onSort,
  page,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  itemLabel = 'items',
  loading,
  fetching,
  emptyMessage = 'No data',
}: DataTableProps<T>) {
  const handleSort = useCallback(
    (columnId: string) => {
      if (!onSort) return
      const newOrder = sortField === columnId && sortOrder === 'asc' ? 'desc' : 'asc'
      onSort(columnId, newOrder)
    },
    [onSort, sortField, sortOrder],
  )

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
      if (!onRowClick || (event.key !== 'Enter' && event.key !== ' ')) return
      event.preventDefault()
      onRowClick(row)
    },
    [onRowClick],
  )

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-busy={loading || fetching || undefined}>
        <thead>
          <tr className="border-b border-[var(--border)]">
            {columns.map((col) => (
              <th
                key={col.id}
                aria-sort={
                  col.sortable && sortField === col.id
                    ? sortOrder === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                className={cn(
                  'px-4 py-3 text-left font-semibold text-[var(--content-secondary)] font-body text-xs uppercase tracking-wider',
                  col.className,
                )}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-sm hover:text-[var(--content)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] select-none cursor-pointer"
                    onClick={() => handleSort(col.id)}
                    aria-label={`${col.header}: sort ${
                      sortField === col.id && sortOrder === 'asc' ? 'descending' : 'ascending'
                    }`}
                  >
                    {col.header}
                    {sortField === col.id && (
                      <span className="text-[var(--brand)]" aria-hidden="true">
                        {sortOrder === 'asc' ? '\u2191' : '\u2193'}
                      </span>
                    )}
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-[var(--content-tertiary)]">
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-[var(--content-tertiary)]">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={keyExtractor(row)}
                className={cn(
                  'border-b border-[var(--border-subtle)] transition-colors',
                  onRowClick &&
                    'cursor-pointer hover:bg-[var(--surface-nested)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand)]',
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (event) => handleRowKeyDown(event, row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'link' : undefined}
                aria-label={onRowClick && rowLabel ? rowLabel(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.id} className={cn('px-4 py-3', col.className)}>
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPages !== undefined && page !== undefined && onPageChange && (totalPages > 1 || totalItems !== undefined) && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          itemLabel={itemLabel}
          onChange={onPageChange}
        />
      )}
    </div>
  )
}

function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  itemLabel,
  onChange,
}: {
  page: number
  totalPages: number
  totalItems?: number
  pageSize?: number
  itemLabel: string
  onChange: (page: number) => void
}) {
  const rangeStart = totalItems === undefined || pageSize === undefined || totalItems === 0
    ? 0
    : (page - 1) * pageSize + 1
  const rangeEnd = totalItems === undefined || pageSize === undefined ? 0 : Math.min(page * pageSize, totalItems)
  const pageNumbers = getVisiblePages(page, totalPages)

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[var(--border)]"
      aria-label={`${itemLabel} pagination`}
    >
      <span className="text-sm text-[var(--content-tertiary)]" aria-live="polite" aria-atomic="true">
        {totalItems !== undefined && pageSize !== undefined
          ? `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${totalItems.toLocaleString()} ${itemLabel}`
          : `Page ${page} of ${totalPages}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          aria-label="Go to previous page"
          className="px-3 py-1.5 text-sm rounded-button bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          Previous
        </button>
        {pageNumbers.map((pageNumber, index) =>
          pageNumber === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-[var(--content-tertiary)]"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={pageNumber}
              type="button"
              onClick={() => onChange(pageNumber)}
              aria-label={`Go to page ${pageNumber}`}
              aria-current={pageNumber === page ? 'page' : undefined}
              className={cn(
                'min-w-8 px-2 py-1.5 text-sm rounded-button transition-colors cursor-pointer',
                pageNumber === page
                  ? 'bg-[var(--brand)] text-white'
                  : 'bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)]',
              )}
            >
              {pageNumber}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Go to next page"
          className="px-3 py-1.5 text-sm rounded-button bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          Next
        </button>
      </div>
    </nav>
  )
}

function getVisiblePages(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    return Array.from({ length: Math.max(totalPages, 0) }, (_, index) => index + 1)
  }

  if (page <= 4) return [1, 2, 3, 4, 5, 'ellipsis', totalPages]
  if (page >= totalPages - 3) {
    return [1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  }

  return [1, 'ellipsis', page - 1, page, page + 1, 'ellipsis', totalPages]
}
